import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback } from "react";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { extractZodTreeErrorProps } from "../../../../utils/errors/extract-zod-tree-error-props";
import { useEnabledChannels } from "../../../channels/hooks/use-enabled-channels";
import { useSegmentFields } from "../../hooks/use-segment-fields";
import { SegmentStateService } from "../../server/segment-state-service";
import {
  recipientTypeSchema,
  recipientTypeSchemaNonRequiredName,
  segmentSchema,
  segmentSchemaNonRequiredName,
} from "../../types/segment-schema";
import { makeDefaultState } from "./reducer/reducer";
import { useSegmentEditor } from "./reducer/use-segment-editor";
import { SegmentManager } from "./segment-manager";
import { SegmentPreview } from "./segment-preview";
import { segmentJsonHasErrors } from "./utils/errors";

import type { UUID } from "../../../../utils/uuid";
import type { Segment as OldSegment } from "../../types";
import type { Segment } from "../../types/segment-schema";
import type { FC, ReactNode } from "react";

export const SegmentManagerWrapper: FC<{
  segment: OldSegment;
  disabled: boolean;
  is_recipient_type: boolean;
  onSave?: (id: UUID | null) => void;
  onClose: (saved: boolean) => void;
  nameRequired?: boolean;
  slots?: { renderContent?: (content: ReactNode) => ReactNode };
}> = ({ segment, disabled, is_recipient_type, onSave, onClose, nameRequired = true, slots }) => {
  const { loading, data } = useSegmentFields();
  const { data: channels, loading: channelsLoading } = useEnabledChannels();

  const isMagicSegment = segment.segment_json.type === "magic";

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const mappedSegment = {
    ...segment,
    description: segment.description ?? "",
    is_default: segment.is_default ?? false,
    segment_json: segment.segment_json,
  } as Segment;

  const schema = is_recipient_type
    ? nameRequired
      ? recipientTypeSchema
      : recipientTypeSchemaNonRequiredName
    : nameRequired
      ? segmentSchema
      : segmentSchemaNonRequiredName;

  const { state, dispatch } = useSegmentEditor(makeDefaultState(schema, mappedSegment), {
    segmentId: mappedSegment.id,
    // View-only users don't connect: the engine can't make a socket read-only yet.
    connect: !disabled,
  });

  const save = useSupabaseCallback(
    async ({ supabase }, saveData: Segment) => {
      await new SegmentStateService(supabase).saveSegment(
        {
          id: saveData.id,
          name: saveData.name,
          description: saveData.description,
          is_default: saveData.is_default,
          segment_json: saveData.segment_json,
          is_recipient_type,
          operation: "AND",
          recipient_type_key: null,
          created_at: segment.created_at,
          deleted_at: segment.deleted_at,
        },
        true,
      );
    },
    [is_recipient_type, segment.created_at, segment.deleted_at],
  );

  const handleSave = useCallback(() => {
    if (state.errors.errors) {
      console.log("Errors exist, cannot save:", state.errors.errors);
      dispatch({ type: "SET_ERRORS_DISPLAY", payload: true });
    } else {
      dispatch({ type: "SET_ERRORS_DISPLAY", payload: false });
      dispatch({ type: "SET_SAVING" });
      void save(state.segment)
        // oxlint-disable-next-line promise/always-return -- side-effect-only then() callback
        .then(() => {
          dispatch({ type: "FINISHED_SAVING", payload: null });
          onClose(true);
          onSave?.(state.segment.id);
        })
        .catch((error: unknown) => {
          console.error("Error saving segment:", error);
          dispatch({ type: "FINISHED_SAVING", payload: "Error saving segment" });
        });
    }
  }, [state, save, onClose, dispatch, onSave]);

  if (loading || channelsLoading) return <LinearProgress />;

  if (isMagicSegment) {
    return (
      <>
        <DialogTitle>Segment builder</DialogTitle>
        <DialogContent>
          <Typography>Please contact support to change</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onClose(false)}
            variant="text"
            data-analytics-id="magic-segment-cancel"
          >
            Cancel
          </Button>
        </DialogActions>
      </>
    );
  }

  const content = (
    <>
      <Stack direction="column" spacing={2}>
        <TextField
          size="small"
          variant="outlined"
          label="Name"
          required={nameRequired}
          value={state.segment.name}
          onChange={(e) => {
            dispatch({ type: "SET_NAME", payload: e.target.value });
          }}
          {...extractZodTreeErrorProps(state.errors, ["name"])}
        />
        <TextField
          size="small"
          variant="outlined"
          label="Description"
          value={state.segment.description ?? ""}
          onChange={(e) => {
            dispatch({ type: "SET_DESCRIPTION", payload: e.target.value });
          }}
          multiline
          {...extractZodTreeErrorProps(state.errors, ["description"])}
        />
      </Stack>
      <SegmentManager
        state={state}
        dispatch={dispatch}
        fields={data ?? []}
        channels={channels ?? []}
        disabled={disabled}
        mode={is_recipient_type ? "AUDIENCE" : "SEGMENT"}
        showPreview
      />
      {!segmentJsonHasErrors(state.errors) && state.segment.segment_json.segments.length > 1 && (
        <SegmentPreview
          segment={state.segment.segment_json}
          channels={channels ?? []}
          type="full"
        />
      )}
    </>
  );

  return (
    <>
      <DialogTitle>Segment builder</DialogTitle>
      <DialogContent>{slots?.renderContent ? slots.renderContent(content) : content}</DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(false)}
          variant="text"
          data-analytics-id="cancel-segment-manager-dialog"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          data-analytics-id="save-segment-manager-dialog"
          disabled={state.saveState.saving}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
};
